import React, { useRef, useEffect, useMemo } from "react";
import {
  attemptFullscreenToggle
} from "../../../../utilities";
import { Sprite, Graphics, Container } from "@pixi/react";
import * as PIXI from "pixi.js";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { openMenu, closeMenu } from "../../../../State/actions";
import {
  selectMenuActive,
  selectCriticalAnimationPlaying,
  selectIsSpinning,
  selectAutoPlayActive,
  selectAnyFeatureTriggeredOrActive,
  selectReplaySessionActive,
} from "../../../../State/selectors";
import { SOUND_NAME } from "../../../../sounds";
import { getNewUITexture } from "../../../../assets";
import ButtonShadow from "./ButtonShadow";
import { PixiSettings } from "../../../../pixiSettings";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { USER_INTERFACE_BUTTON_CONFIG } from "../../../../pixiSettings";
import { useSecondaryToolbar } from "../../../../Hooks/useSecondaryToolbar";
import { soundManager } from "../../../../SoundManager";
import { useButtonBrightness } from "../../../../Hooks/useButtonBrightness";

const ButtonMenu = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { getResponsiveConfig, device, orientation, dimensions, isMobileSmall } = useDimensions();
  const toolbar = useSecondaryToolbar();
  const menuActive = useSelector(selectMenuActive);
  const criticalAnimationPlaying = useSelector(selectCriticalAnimationPlaying);
  const isSpinning = useSelector(selectIsSpinning);
  const autoPlayActive = useSelector(selectAutoPlayActive);
  const anyFeatureTriggeredOrActive = useSelector(selectAnyFeatureTriggeredOrActive);
  const replaySessionActive = useSelector(selectReplaySessionActive);

  // Calculate if button should be disabled - prevent during spins, critical
  // animations, features (including the trigger/intro/transition phase),
  // autoplay, or replay
  const isDisabled =
    criticalAnimationPlaying ||
    isSpinning ||
    anyFeatureTriggeredOrActive ||
    autoPlayActive ||
    replaySessionActive;

  // Add ref to track if button is currently processing a click
  const isProcessingClickRef = useRef(false);

  // Add ref to store timeout ID for proper cleanup
  const debounceTimeoutRef = useRef(null);

  // Shared hover/press hook
  const { buttonRef, handlePointerOver, handlePointerOut, triggerPress, cleanupFilters, alpha, cursor } =
    useButtonBrightness({ disabled: isDisabled });


  const texture = getNewUITexture("icon_menu");

  // Override shared config — menu button is slightly smaller on mobile portrait
  const MENU_BUTTON_CONFIG = useMemo(() => ({
    ...USER_INTERFACE_BUTTON_CONFIG,
    DEVICE_CONFIGS: {
      ...USER_INTERFACE_BUTTON_CONFIG.DEVICE_CONFIGS,
      [PixiSettings.DEVICE.MOBILE]: {
        ...USER_INTERFACE_BUTTON_CONFIG.DEVICE_CONFIGS[PixiSettings.DEVICE.MOBILE],
        [PixiSettings.ORIENTATION.PORTRAIT]: {
          deviceMultiplier: 2.2,
        },
      },
    },
  }), []);

  // Get responsive configuration from context - memoized to prevent recalculation
  const buttonConfig = useMemo(
    () => getResponsiveConfig(MENU_BUTTON_CONFIG, true),
    [getResponsiveConfig, MENU_BUTTON_CONFIG]
  );

  // Calculate responsive button position based on device and orientation - memoized
  const buttonPosition = useMemo(() => {
    if (isMobileSmall) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return {
          x: buttonConfig.width * 0.925,
          y: buttonConfig.height * 1.3
        };
      } else {
        return {
          x: buttonConfig.width * 0.6,
          y: dimensions.height - toolbar.height / 2
        };
      }
    }
    if (device === PixiSettings.DEVICE.MOBILE) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return {
          x: buttonConfig.width * 0.85,
          y: buttonConfig.height * 1.6
        };
      } else {
        // landscape — center vertically in the bottom toolbar
        return {
          x: buttonConfig.width * 0.6,
          y: dimensions.height - toolbar.height / 2
        };
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return {
          x: buttonConfig.width * 0.8,
          y: buttonConfig.height * 0.8
        };
      } else {
        return {
          x: buttonConfig.width * 0.8,
          y: buttonConfig.height * 0.8
        };
      }
    } else {
      return {
        x: buttonConfig.width,
        y: dimensions.height - toolbar.height / 2
      };
    }
  }, [device, orientation, dimensions.height, buttonConfig.width, buttonConfig.height, toolbar.height, isMobileSmall]);

  const handleClick = () => {
    // Disable button during critical animations or spinning
    if (isDisabled) {
      return;
    }

    // Attempt to toggle fullscreen if conditions are met
    attemptFullscreenToggle(dispatch, device);

    // Prevent rapid clicks if already processing
    if (isProcessingClickRef.current) {
      return;
    }

    // Clear any existing timeout before starting new one
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    isProcessingClickRef.current = true;

    soundManager.play(SOUND_NAME.button_click);

    // Add press animation before toggling menu
    triggerPress({
        targetWidth: buttonConfig.width,
        targetHeight: buttonConfig.height
      });

    if (menuActive) {
      dispatch(closeMenu());
    } else {
      dispatch(openMenu());
    }

    // Reset processing flag after animation completes (500ms debounce)
    debounceTimeoutRef.current = setTimeout(() => {
      isProcessingClickRef.current = false;
    }, 500);
  };

  // Remove hover effects when button becomes disabled
  useEffect(() => {
    if (isDisabled && buttonRef.current) {
      buttonRef.current.filters = [];    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDisabled]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      cleanupFilters();
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [cleanupFilters]);

  // Calculate button radius to match the visual sprite exactly - memoized
  const buttonRadius = useMemo(
    () => buttonConfig.width / 2,
    [buttonConfig.width]
  );

  return (
    <Container
      name="secondary_button_menu"
      x={buttonPosition.x}
      y={buttonPosition.y}
      zIndex={1}
    >
      {/* Interactive circular area - handles all pointer events */}
      <Graphics
        name="button_menu_interactive_area"
        eventMode="static"
        cursor={cursor}
        hitArea={new PIXI.Circle(0, 0, buttonRadius)}
        onpointertap={handleClick}
        onpointerover={handlePointerOver}
        onpointerout={handlePointerOut}
        draw={(g) => {
          g.clear();
          // Nearly invisible fill (required for hit detection to work)
          g.beginFill(0x000000, 0.01);
          g.drawCircle(0, 0, buttonRadius);
          g.endFill();
        }}
      />

      {/* Visual sprite (non-interactive, just for display) */}
      {/* Soft drop shadow under it (ButtonShadow, utils/buttonShadow) */}
      <ButtonShadow target={buttonRef} texture={texture} width={buttonConfig.width} height={buttonConfig.height} />
      <Sprite
        ref={buttonRef}
        texture={texture}
        name="button_menu_icon"
        anchor={0.5}
        width={buttonConfig.width}
        height={buttonConfig.height}
        x={0}
        y={0}
        eventMode="none"
        alpha={alpha}
        accessibleTitle={t("open_menu")}
        accessibleHint={t("open_menu")}
        roundPixels={true}
      />
    </Container>
  );
};

export default ButtonMenu;
